import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authService } from 'auth/services';

import { useIsAuthenticated } from '@azure/msal-react';

import { fetchOrderPreAppointments, updateAppointmentDateAndTime } from 'features/order-tracker/store/actions';
import { getAmendAppointmentInfo, getSelectedAppointmentDate } from 'features/order-tracker/store/selectors';
import {
  formatSelectedWeekdayValue,
  getMonthInfo,
  getMonthName,
  getMonthNumByName,
} from 'features/order-tracker/utils';
import { MONTHS_IN_YEAR } from 'features/order-tracker/utils/constants';

import { DatePicker, MonthSelector } from './components';

import styles from './styles.module.scss';

export const Calendar: React.FC = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const { firstAvailableAppointmentDate } = useSelector(getAmendAppointmentInfo);
  const selectedDate = useSelector(getSelectedAppointmentDate);
  const [monthInfo, setMonthInfo] = useState(getMonthInfo(0, firstAvailableAppointmentDate));
  const [monthOffset, setMonthOffset] = useState(0);

  useEffect(() => {
    const { month: prevMonth, year: prevYear } = monthInfo;
    const currentDate = new Date(selectedDate);
    const currentMonthNum = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    if (selectedDate && prevMonth !== getMonthName(currentMonthNum)) {
      const prevMonthNum = getMonthNumByName(prevMonth);
      const newMonth =
        currentYear > prevYear ? MONTHS_IN_YEAR + currentMonthNum - prevMonthNum : currentMonthNum - prevMonthNum;
      setMonthInfo(getMonthInfo(newMonth, firstAvailableAppointmentDate));
      setMonthOffset(newMonth);
    }
  }, [selectedDate]);

  const pickMonth = (isNextMonth?: boolean): void => {
    const newMonth = isNextMonth ? monthOffset + 1 : monthOffset - 1;
    const newMonthInfo = getMonthInfo(newMonth, firstAvailableAppointmentDate);
    const { year, month, firstAvailableDateInMonth } = newMonthInfo;
    const formattedDate = formatSelectedWeekdayValue(year, month, newMonth ? 1 : firstAvailableDateInMonth);

    setMonthInfo(newMonthInfo);
    setMonthOffset(newMonth);

    isAuthenticated && authService.throttledRefreshToken();
    dispatch(fetchOrderPreAppointments({ date: formattedDate }));
    dispatch(
      updateAppointmentDateAndTime({
        selectedDate: '',
        selectedSlot: '',
      }),
    );
  };

  return (
    <div className={styles.calendarContainer}>
      <MonthSelector monthInfo={monthInfo} monthOffset={monthOffset} pickNewMonth={pickMonth} />
      <DatePicker monthInfo={monthInfo} monthOffset={monthOffset} />
    </div>
  );
};
